import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { IcoSave } from '../dashboard/DashIcons';
import '../../styles/admin-dashboard.css';

export default function OrgSettings() {
  const { user } = useAuth();
  const { settings: currentSettings, features: currentFeatures, refetch } = useOrgSettings();
  const orgId = user?.organizationId;
  const token = user?.token;

  const [form, setForm] = useState({
    lunchMode: 'minutes',
    lunchFlatRatePerEntry: 0,
    managerCanApproveSchedules: false,
    managerCanManageTeam: false,
    roleNames: { manager: 'Manager', employee: 'Employee' },
    sessionTimeoutMinutes: parseInt(localStorage.getItem('inactivityTimeoutMinutes') || '30', 10),
    ptoPolicy: {
      calculationType: 'frontload',
      hours: { fulltime: 80, parttime: 40, salaried: 80 },
      frontloadResetMonth: 1, accrualPeriod: 'biweekly', carryoverLimit: 0,
    },
    sickPolicy: {
      calculationType: 'frontload',
      hours: { fulltime: 40, parttime: 20, salaried: 40 },
      frontloadResetMonth: 1, accrualPeriod: 'biweekly', carryoverLimit: 0,
    },
    features: {
      clockInOut: false, scheduling: false, payroll: false, reporting: false,
      ptoTracking: false, mobileApp: false, evvCompliance: false,
    },
  });
  const [saveStatus, setSaveStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Sync form with fetched settings
  useEffect(() => {
    setForm(prev => ({
      managerCanApproveSchedules: currentSettings.managerCanApproveSchedules,
      managerCanManageTeam: currentSettings.managerCanManageTeam,
      roleNames: {
        manager: currentSettings.roleNames.manager,
        employee: currentSettings.roleNames.employee
      },
      lunchMode: currentSettings.lunchSettings?.mode || 'minutes',
      lunchFlatRatePerEntry: currentSettings.lunchSettings?.flatRatePerEntry || 0,
      sessionTimeoutMinutes: prev.sessionTimeoutMinutes,
      ptoPolicy: currentSettings.ptoPolicy || prev.ptoPolicy,
      sickPolicy: currentSettings.sickPolicy || prev.sickPolicy,
      features: {
        clockInOut: currentFeatures?.clockInOut ?? false,
        scheduling: currentFeatures?.scheduling ?? false,
        payroll: currentFeatures?.payroll ?? false,
        reporting: currentFeatures?.reporting ?? false,
        ptoTracking: currentFeatures?.ptoTracking ?? false,
        mobileApp: currentFeatures?.mobileApp ?? false,
        evvCompliance: currentFeatures?.evvCompliance ?? false,
      },
    }));
  }, [currentSettings, currentFeatures]); // ← closing brace was missing

  // ── these are now correctly OUTSIDE the useEffect ──

  const handleToggle = (field) => {
    setForm(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleFeatureToggle = (key) => {
    setForm(prev => ({ ...prev, features: { ...prev.features, [key]: !prev.features[key] } }));
  };

  const handleRoleName = (role, value) => {
    setForm(prev => ({ ...prev, roleNames: { ...prev.roleNames, [role]: value } }));
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveStatus(null);
    try {
      const res = await fetch(`/api/organizations/${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          managerCanApproveSchedules: form.managerCanApproveSchedules,
          managerCanManageTeam: form.managerCanManageTeam,
          roleNames: form.roleNames,
          ptoPolicy: form.ptoPolicy,
          sickPolicy: form.sickPolicy,
          features: form.features,
        })
      });

      // Save lunch settings — only relevant (and only allowed server-side) when payroll is enabled
      if (form.features.payroll) await fetch('/api/payroll/lunch-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          mode: form.lunchMode,
          flatRatePerEntry: parseFloat(form.lunchFlatRatePerEntry) || 0
        })
      });

      if (res.ok) {
        const mins = parseInt(form.sessionTimeoutMinutes, 10);
        if (!isNaN(mins) && mins >= 1) {
          localStorage.setItem('inactivityTimeoutMinutes', String(mins));
        }
        setSaveStatus('saved');
        refetch();
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => setSaveStatus(null), 3500);
    }
  };

  const Toggle = ({ checked, onChange, label, description }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '1.25rem 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ flex: 1, paddingRight: '2rem' }}>
        <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', marginBottom: '0.25rem' }}>{label}</div>
        <div style={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: '1.5' }}>{description}</div>
      </div>
      <button
        onClick={onChange}
        style={{
          width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
          background: checked ? '#22c55e' : '#e5e7eb',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0
        }}
      >
        <span style={{
          position: 'absolute', top: '3px',
          left: checked ? '25px' : '3px',
          width: '20px', height: '20px', borderRadius: '50%', background: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
        }} />
      </button>
    </div>
  );

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1>Organization Settings</h1>
          <p className="subtitle">Configure permissions and role names for your organization</p>
        </section>

        <div style={{ maxWidth: '720px', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* ── Manager Permissions ── */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827', marginBottom: '0.25rem' }}>
              Manager Permissions
            </h2>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
              Control what managers can do across your organization. Changes apply immediately to all managers.
            </p>
            <Toggle
              checked={form.managerCanApproveSchedules}
              onChange={() => handleToggle('managerCanApproveSchedules')}
              label="Managers can approve schedules"
              description="When enabled, managers must approve their team's schedule before it can be submitted to payroll."
            />
            <Toggle
              checked={form.managerCanManageTeam}
              onChange={() => handleToggle('managerCanManageTeam')}
              label="Managers can add/remove team members"
              description="When enabled, managers can assign and unassign employees from their own team without super admin involvement."
            />
          </div>

          {/* ── Features ── */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827', marginBottom: '0.25rem' }}>
              Features
            </h2>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
              Choose which modules your dashboard shows. Time Tracking and Team Management are always included.
            </p>
            {[
              { key: 'clockInOut', label: 'Clock In / Clock Out', description: 'Employees clock in and out from the dashboard.' },
              { key: 'scheduling', label: 'Scheduling', description: 'Build and publish weekly shift schedules.' },
              { key: 'payroll', label: 'Payroll', description: 'Run payroll and export pay period totals.' },
              { key: 'reporting', label: 'Reporting & Analytics', description: 'Time, labor, and attendance reports.' },
              { key: 'ptoTracking', label: 'PTO & Sick Time Tracking', description: 'Track and approve paid time off and sick leave.' },
              { key: 'mobileApp', label: 'Mobile App Access', description: 'Let employees use the mobile app to clock in/out.' },
              { key: 'evvCompliance', label: 'EVV Compliance', description: 'Electronic Visit Verification for home care visits.' },
              // Integrations is a paid one-time add-on — purchased from Settings → Integrations & API, not toggled here.
            ].map(opt => (
              <Toggle
                key={opt.key}
                checked={form.features[opt.key]}
                onChange={() => handleFeatureToggle(opt.key)}
                label={opt.label}
                description={opt.description}
              />
            ))}
          </div>

          {/* ── Lunch Deduction — only relevant when payroll is enabled ── */}
          {form.features.payroll && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827', marginBottom: '0.25rem' }}>
              Lunch Deduction
            </h2>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: '1.25rem' }}>
              Choose how lunch breaks are deducted from employee pay.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { value: 'minutes', label: 'Deduct by minutes', desc: 'Lunch minutes per shift are converted to hours and subtracted from worked time.' },
                { value: 'dollars', label: 'Flat dollar deduction per entry', desc: 'A fixed dollar amount is deducted per time entry regardless of hours.' }
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', gap: '0.875rem', alignItems: 'flex-start', padding: '1rem', background: form.lunchMode === opt.value ? '#f0f4ff' : '#f9fafb', border: `2px solid ${form.lunchMode === opt.value ? '#22c55e' : '#e5e7eb'}`, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s' }}>
                  <input
                    type="radio"
                    name="lunchMode"
                    value={opt.value}
                    checked={form.lunchMode === opt.value}
                    onChange={() => setForm(prev => ({ ...prev, lunchMode: opt.value }))}
                    style={{ marginTop: '0.25rem', accentColor: '#22c55e' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#111827' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem', lineHeight: '1.5' }}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {form.lunchMode === 'dollars' && (
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#374151', marginBottom: '0.4rem' }}>
                  Flat deduction per entry ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.lunchFlatRatePerEntry}
                  onChange={e => setForm(prev => ({ ...prev, lunchFlatRatePerEntry: e.target.value }))}
                  placeholder="e.g. 5.00"
                  style={{ width: '160px', padding: '0.65rem 0.875rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#22c55e'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
                <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.35rem' }}>
                  This amount will appear as a separate line item on each employee's payroll entry.
                </p>
              </div>
            )}
          </div>
          )}

          {/* ── Role Names ── */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827', marginBottom: '0.25rem' }}>
              Custom Role Names
            </h2>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: '1.25rem' }}>
              Rename roles to match your organization's terminology.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#374151', marginBottom: '0.4rem' }}>Manager role label</label>
                <input
                  type="text"
                  value={form.roleNames.manager}
                  onChange={e => handleRoleName('manager', e.target.value)}
                  placeholder="Manager"
                  maxLength={50}
                  style={{ width: '100%', padding: '0.65rem 0.875rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#22c55e'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
                <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.3rem' }}>e.g. Supervisor, Team Lead, Coordinator</p>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#374151', marginBottom: '0.4rem' }}>Employee role label</label>
                <input
                  type="text"
                  value={form.roleNames.employee}
                  onChange={e => handleRoleName('employee', e.target.value)}
                  placeholder="Employee"
                  maxLength={50}
                  style={{ width: '100%', padding: '0.65rem 0.875rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#22c55e'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
                <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.3rem' }}>e.g. Caregiver, Associate, Staff</p>
              </div>
            </div>

            {/* Live preview */}
            <div style={{ marginTop: '1.25rem', padding: '0.875rem 1rem', background: '#f8f9ff', borderRadius: '8px', border: '1px solid #e0e7ff' }}>
              <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Preview</p>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <span style={{ padding: '0.25rem 0.75rem', background: '#e0e7ff', color: '#4338ca', borderRadius: '99px', fontSize: '0.8rem', fontWeight: '600' }}>{form.roleNames.manager || 'Manager'}</span>
                <span style={{ padding: '0.25rem 0.75rem', background: '#f3f4f6', color: '#374151', borderRadius: '99px', fontSize: '0.8rem', fontWeight: '600' }}>{form.roleNames.employee || 'Employee'}</span>
                <span style={{ padding: '0.25rem 0.75rem', background: '#fef3c7', color: '#92400e', borderRadius: '99px', fontSize: '0.8rem', fontWeight: '600' }}>Super Admin</span>
              </div>
            </div>
          </div>

          {/* ── PTO Policy ── */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827', marginBottom: '0.25rem' }}>
              PTO &amp; Sick Time Policy
            </h2>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: '1.5rem' }}>
              Control how PTO and sick time are calculated for all employees.
            </p>
            <OrgPolicySection
              title="PTO"
              policy={form.ptoPolicy}
              onChange={patch => setForm(prev => ({ ...prev, ptoPolicy: { ...prev.ptoPolicy, ...patch } }))}
            />
            <div style={{ borderTop: '1px solid #f3f4f6', margin: '1.25rem 0' }} />
            <OrgPolicySection
              title="Sick Time"
              policy={form.sickPolicy}
              onChange={patch => setForm(prev => ({ ...prev, sickPolicy: { ...prev.sickPolicy, ...patch } }))}
            />
          </div>

          {/* ── Session Timeout ── */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827', marginBottom: '0.25rem' }}>
              Inactivity Timeout
            </h2>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: '1.25rem' }}>
              How many minutes of no mouse movement or typing before the "Still there?" prompt appears.
              The session will auto-logout 5 minutes after the prompt if there's no response.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
              <input
                type="number"
                min="5"
                max="480"
                value={form.sessionTimeoutMinutes}
                onChange={e => setForm(prev => ({ ...prev, sessionTimeoutMinutes: e.target.value }))}
                style={{ width: '100px', padding: '0.65rem 0.875rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = '#22c55e'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <span style={{ fontSize: '0.9rem', color: '#374151', fontWeight: '500' }}>minutes</span>
              <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>(min 5 · max 480)</span>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.5rem' }}>
              Current setting: {form.sessionTimeoutMinutes} min idle → prompt · then 5 min to respond before auto-logout
            </p>
          </div>

          {/* ── Save ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={handleSave}
              disabled={loading}
              style={{ padding: '0.75rem 2rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.95rem', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Saving...' : <><IcoSave size={15}/> Save Settings</>}
            </button>
            {saveStatus === 'saved' && <span style={{ color: '#059669', fontWeight: '600', fontSize: '0.9rem' }}>Settings saved!</span>}
            {saveStatus === 'error' && <span style={{ color: '#ef4444', fontWeight: '600', fontSize: '0.9rem' }}>Error saving. Please try again.</span>}
          </div>

        </div>
      </div>
    </div>
  );
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const EMP_TYPES_ORG = [
  { key: 'fulltime', label: 'Full-time' },
  { key: 'parttime', label: 'Part-time' },
  { key: 'salaried', label: 'Salaried'  },
];

function OrgPolicySection({ title, policy, onChange }) {
  const isFrontload = policy.calculationType === 'frontload';
  const hours = policy.hours || { fulltime: 0, parttime: 0, salaried: 0 };
  const iStyle = {
    width: '100%', padding: '0.6rem 0.75rem', border: '1.5px solid #e5e7eb',
    borderRadius: '8px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  };
  const lStyle = { display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#374151', marginBottom: '0.3rem' };

  return (
    <div>
      <p style={{ fontWeight: '700', fontSize: '0.9rem', color: '#374151', marginBottom: '0.75rem' }}>{title}</p>

      {/* Calculation type */}
      <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1rem' }}>
        {[
          { value: 'frontload', label: 'Front-load', desc: 'Full allocation given at reset date.' },
          { value: 'accrual',   label: 'Accrual',    desc: 'Hours earned each pay period.' },
        ].map(opt => (
          <label key={opt.value} style={{ flex: 1, display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.75rem', background: policy.calculationType === opt.value ? '#f0f4ff' : '#f9fafb', border: `2px solid ${policy.calculationType === opt.value ? '#22c55e' : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer' }}>
            <input type="radio" value={opt.value} checked={policy.calculationType === opt.value} onChange={() => onChange({ calculationType: opt.value })} style={{ marginTop: '0.15rem', accentColor: '#22c55e' }} />
            <div>
              <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#111827' }}>{opt.label}</div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.15rem' }}>{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>

      {/* Hours per employment type */}
      <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '0.875rem', marginBottom: '0.875rem', border: '1px solid #e5e7eb' }}>
        <p style={{ margin: '0 0 0.65rem', fontSize: '0.78rem', fontWeight: '700', color: '#374151' }}>
          {isFrontload ? 'Annual hours by employment type' : 'Hours earned per period by employment type'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          {EMP_TYPES_ORG.map(({ key, label }) => (
            <div key={key}>
              <label style={lStyle}>{label}</label>
              <input type="number" min="0" step="0.5" value={hours[key] ?? 0}
                onChange={e => onChange({ hours: { ...hours, [key]: parseFloat(e.target.value) || 0 } })}
                style={iStyle} />
            </div>
          ))}
        </div>
      </div>

      {/* Period / reset + carryover */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {isFrontload ? (
          <div>
            <label style={lStyle}>Reset month</label>
            <select value={policy.frontloadResetMonth ?? 1} onChange={e => onChange({ frontloadResetMonth: parseInt(e.target.value) })} style={iStyle}>
              {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label style={lStyle}>Accrual period</label>
            <select value={policy.accrualPeriod ?? 'biweekly'} onChange={e => onChange({ accrualPeriod: e.target.value })} style={iStyle}>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        )}
        <div>
          <label style={lStyle}>Carryover limit (hrs)</label>
          <input type="number" min="-1" step="0.5" value={policy.carryoverLimit ?? 0} onChange={e => onChange({ carryoverLimit: parseFloat(e.target.value) ?? 0 })} style={iStyle} />
          <p style={{ fontSize: '0.68rem', color: '#9ca3af', margin: '0.2rem 0 0' }}>0 = none · -1 = unlimited</p>
        </div>
      </div>
    </div>
  );
}